/** MSC Finance — relationships (economic parent / manager / affiliate) and compensation components with budget. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';
import { Card, PageHeader, TenantSelect, btnSmall, errorText, inputCls, useTenantScope } from '../../components/finance/financeUi';
import { RelationshipsPanel } from '../../components/economics/RelationshipsPanel';
import { BudgetPanel, ComponentForm, ComponentsList } from '../../components/economics/Components';

export default function EconomicRelationshipsPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const [people, setPeople] = useState([]);
  const [sel, setSel] = useState('');
  const [rel, setRel] = useState(null);
  const [budget, setBudget] = useState(null);
  const [comps, setComps] = useState([]);
  const [legacy, setLegacy] = useState([]);
  useEffect(() => {
    if (!scope.tenantId) return undefined;
    let live = true;
    setSel(''); setPeople([]);
    api.request(`/economic-relationships/people?tenant_id=${scope.tenantId}`).then((p) => live && setPeople(p)).catch(() => live && setPeople([]));
    api.request(`/economic-relationships/legacy-review?tenant_id=${scope.tenantId}`).then((l) => live && setLegacy(l)).catch(() => live && setLegacy([]));
    return () => { live = false; };
  }, [scope.tenantId]);
  const load = useCallback(() => {
    if (!sel) { setRel(null); setBudget(null); setComps([]); return; }
    api.request(`/economic-relationships/users/${sel}`).then((r) => setRel({ ...r, subject: `user:${sel}` })).catch((e) => toast.error(errorText(e)));
    api.request(`/economic-relationships/budget/${sel}`).then(setBudget).catch(() => setBudget(null));
    api.request(`/economic-relationships/components?subject_user_id=${sel}`).then(setComps).catch(() => setComps([]));
  }, [sel]);
  useEffect(load, [load]);
  const scan = async () => {
    try { const r = await api.request('/economic-relationships/legacy-review/scan', { method: 'POST', body: JSON.stringify({ tenant_id: scope.tenantId }) }); toast.success(`${r.flagged.length} legacy arrangement(s) flagged for review`); setLegacy(await api.request(`/economic-relationships/legacy-review?tenant_id=${scope.tenantId}`)); } catch (e) { toast.error(errorText(e)); }
  };
  const names = Object.fromEntries(people.map((p) => [p.user_id, p.name]));
  const refNames = Object.fromEntries(people.map((p) => [`user:${p.user_id}`, p.name]));
  const managerRefs = new Set((rel?.operational_managers || []).map((m) => m.from_entity_id));
  const recipients = people.map((p) => ({ ref: `user:${p.user_id}`, label: `${managerRefs.has(`user:${p.user_id}`) ? 'Manager: ' : ''}${p.name}`, manager: managerRefs.has(`user:${p.user_id}`) }));
  return (
    <div className="space-y-6" data-testid="economic-relationships-page">
      <PageHeader title="Relationships & Compensation Components" subtitle="Economic parent, operational manager and affiliate referrer are separate relationships. Management overrides and bonuses are saved and checked against MSC's budget, but are not yet calculated into payouts.">
        <TenantSelect scope={scope} testid="econ-tenant" />
      </PageHeader>
      <Card title={`Legacy economics review (${legacy.length})`} testid="legacy-card" aside={<button type="button" className={btnSmall} onClick={scan} data-testid="legacy-scan-btn">Check existing arrangements</button>}>
        <p className="px-5 py-3 text-xs text-muted-foreground" data-testid="legacy-summary">{legacy.length ? legacy.map((l) => `${names[l.subject_user_id] || l.subject_user_id} (${l.budget.msc_cost_pct}%)`).join(' · ') : 'No existing arrangements flagged.'} Existing payouts continue; only new or worse changes are checked.</p>
      </Card>
      <Card title="Person" testid="econ-person-card" aside={
        <select className={`${inputCls} max-w-64`} value={sel} onChange={(e) => setSel(e.target.value)} data-testid="econ-person-select">
          <option value="">Choose a person…</option>
          {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name} · {p.role.replace('iso_', '')}</option>)}
        </select>}>
        {sel ? <RelationshipsPanel rel={rel} names={names} onChange={load} /> : <p className="px-5 py-4 text-sm text-muted-foreground">Pick a person to see their relationships and components.</p>}
      </Card>
      {sel && <Card title="MSC compensation budget" testid="econ-budget-card"><BudgetPanel budget={budget} /></Card>}
      {sel && <Card title="Compensation components" testid="econ-components-card"><ComponentsList rows={comps} names={refNames} /><div className="border-t"><ComponentForm subjectId={sel} recipients={recipients} onSaved={load} /></div></Card>}
    </div>
  );
}
